import React from "react";

// ── Helpers ───────────────────────────────────────────────────────────────────
function createFieldId(id: string | undefined, prefix: string, label: string) {
  return id ?? `${prefix}-${label.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`;
}

function getDescribedBy(...ids: (string | undefined)[]): string | undefined {
  const value = ids.filter(Boolean).join(" ");
  return value || undefined;
}

// ── StepHeader ────────────────────────────────────────────────────────────────
interface StepHeaderProps {
  focusRef: React.RefObject<HTMLHeadingElement>;
  ariaLabel: string;
  description?: string;
  showRequiredInfo?: boolean;
}

export function StepHeader({
  focusRef,
  ariaLabel,
  description,
  showRequiredInfo = true,
}: StepHeaderProps) {
  return (
    <>
      <h2 ref={focusRef} tabIndex={-1} className="sr-only">
        {ariaLabel}
      </h2>

      {showRequiredInfo && (
        <div>
          <small>Obligatoriska fält är markerade med en asterisk.</small>
          <span className="ms-1 invalid" aria-hidden="true">
            *
          </span>
        </div>
      )}

      {description && (
        <div
          className="mt-5"
          dangerouslySetInnerHTML={{ __html: description }}
        />
      )}
    </>
  );
}

// ── FieldLabel ────────────────────────────────────────────────────────────────
interface FieldLabelProps {
  htmlFor: string;
  label: string;
  required?: boolean;
}

export function FieldLabel({ htmlFor, label, required }: FieldLabelProps) {
  return (
    <label htmlFor={htmlFor}>
      {label}
      {required && (
        <span className="ms-1 invalid" aria-hidden="true">
          *
        </span>
      )}
    </label>
  );
}

// ── ValidationMessage ─────────────────────────────────────────────────────────
interface ValidationMessageProps {
  hasError?: boolean;
  message?: string;
}

export function ValidationMessage({
  hasError,
  message = "Fältet är obligatoriskt.",
}: ValidationMessageProps) {
  if (!hasError) return null;
  return (
    <small className="invalid" role="alert">
      {message}
    </small>
  );
}

// ── TextInput ─────────────────────────────────────────────────────────────────
interface TextInputProps {
  label: string;
  value?: string;
  onChange: React.ChangeEventHandler<HTMLInputElement>;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  type?: React.HTMLInputTypeAttribute;
  required?: boolean;
  pattern?: string;
  id?: string;
  describedBy?: string;
  hasError?: boolean;
  helpText?: React.ReactNode;
  errorMessage?: string;
}

export function TextInput({
  label,
  value = "",
  onChange,
  onBlur,
  type = "text",
  required,
  pattern,
  id,
  describedBy,
  hasError,
  helpText,
  errorMessage,
}: TextInputProps) {
  const inputId = createFieldId(id, "input", label);
  const helpId = helpText ? `${inputId}-help` : undefined;

  return (
    <div>
      <FieldLabel htmlFor={inputId} label={label} required={required} />
      <input
        id={inputId}
        type={type}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        required={required}
        pattern={pattern}
        aria-required={required || undefined}
        aria-describedby={getDescribedBy(describedBy, helpId)}
        aria-invalid={hasError || undefined}
        className={hasError ? "input-validation-error" : undefined}
      />
      {helpText && (
        <small id={helpId} className="form-text">
          {helpText}
        </small>
      )}
      <br />
      <ValidationMessage hasError={hasError} message={errorMessage} />
    </div>
  );
}

// ── TextArea ──────────────────────────────────────────────────────────────────
interface TextAreaProps {
  label: string;
  value?: string;
  onChange: React.ChangeEventHandler<HTMLTextAreaElement>;
  onBlur?: React.FocusEventHandler<HTMLTextAreaElement>;
  id?: string;
  describedBy?: string;
  rows?: number;
  maxLength?: number;
  required?: boolean;
  hasError?: boolean;
  helpText?: React.ReactNode;
}

export function TextArea({
  label,
  value = "",
  onChange,
  onBlur,
  id,
  describedBy,
  rows = 5,
  maxLength,
  required,
  hasError,
  helpText,
}: TextAreaProps) {
  const inputId = createFieldId(id, "textarea", label);
  const helpId = helpText ? `${inputId}-help` : undefined;

  return (
    <div>
      <FieldLabel htmlFor={inputId} label={label} required={required} />
      <textarea
        id={inputId}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        maxLength={maxLength}
        required={required}
        rows={rows}
        aria-required={required || undefined}
        aria-describedby={getDescribedBy(describedBy, helpId)}
        aria-invalid={hasError || undefined}
        className={hasError ? "vgr-form__textarea--error" : undefined}
      />
      {helpText && (
        <small id={helpId} className="form-text">
          {helpText}
        </small>
      )}
      <br />
      <ValidationMessage hasError={hasError} />
    </div>
  );
}

// ── FormField (för custom components, t.ex. react-select) ─────────────────────
interface FormFieldProps {
  label: string;
  inputId: string;
  required?: boolean;
  hasError?: boolean;
  errorMessage?: string;
  helpText?: React.ReactNode;
  children: React.ReactNode;
}

export function FormField({
  label,
  inputId,
  required,
  hasError,
  errorMessage,
  helpText,
  children,
}: FormFieldProps) {
  const helpId = helpText ? `${inputId}-help` : undefined;

  return (
    <div>
      <FieldLabel htmlFor={inputId} label={label} required={required} />
      <div
        aria-required={required || undefined}
        aria-invalid={hasError || undefined}
        aria-describedby={helpId}
      >
        {children}
      </div>
      {helpText && (
        <small id={helpId} className="form-text">
          {helpText}
        </small>
      )}
      <ValidationMessage hasError={hasError} message={errorMessage} />
    </div>
  );
}

// RequiredCheckboxes─────────────────────────────────────────────────────────────
interface RequiredCheckboxProps {
  id: string;
  checked: boolean;
  label: React.ReactNode;
  hasError?: boolean;
  onChange: (checked: boolean) => void;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
}

export function RequiredCheckbox({
  id,
  checked,
  onChange,
  label,
  hasError,
  onBlur,
}: RequiredCheckboxProps) {
  return (
    <>
      <div className={`activity__checkboxes ${hasError ? "vgr-form__group--error" : ""}`}>
        <label htmlFor={id}>
          <input
            id={id}
            type="checkbox"
            checked={checked}
            onChange={(e) => onChange(e.target.checked)}
            onBlur={onBlur}
            required
            aria-invalid={hasError || undefined}
          />
          <span className="visually-hidden">Alternativ:</span>
          {label}
          <span className="invalid" aria-hidden="true">
            *
          </span>
        </label>

      </div>
      <ValidationMessage hasError={hasError} />
    </>
  );
}
